import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import { cva } from 'class-variance-authority';
import { cn } from '@/shared/utils/cn.js';
import { ChevronDown, Check, Loader2, Settings2, Search } from 'lucide-react';

const selectVariants = cva(
  'border-tertiary-20 text-tertiary-100 hover:border-tertiary-40 focus-within:border-primary-100 focus-within:ring-primary-100/20 relative w-full cursor-pointer rounded-lg border bg-white transition-all duration-200 focus-within:ring-1 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600',
  {
    variants: {
      size: {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-base',
        lg: 'px-5 py-3 text-lg',
      },
      disabled: {
        true: 'bg-tertiary-10 cursor-not-allowed opacity-50 dark:bg-gray-800/50',
        false: '',
      },
      error: {
        true: '!border-red-500 hover:!border-red-600',
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      disabled: false,
      error: false,
    },
  },
);

const optionVariants = cva(
  'text-tertiary-100 hover:bg-tertiary-10 flex cursor-pointer items-center justify-between px-4 py-2.5 transition-colors duration-150 dark:text-gray-200 dark:hover:bg-gray-700',
  {
    variants: {
      selected: {
        true: '!bg-primary-10/50 !text-primary-100 hover:!bg-primary-10 dark:!bg-primary-900/30 dark:!text-primary-400 dark:hover:!bg-primary-900/40',
        false: '',
      },
    },
    defaultVariants: {
      selected: false,
    },
  },
);

const getErrorMessage = (error) => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error.message) return error.message;
  return String(error);
};

function Select({
  options = [],
  value,
  onChange,
  placeholder = 'انتخاب کنید...',
  label,
  error,
  disabled = false,
  size = 'md',
  className,
  renderOption,
  renderValue,
  loading = false,
  emptyMessage = 'موردی یافت نشد',
  showManageButton = false,
  onManageClick,
  manageButtonText = 'مدیریت',
  icon,
  showSearch = false,
  searchValue,
  onSearchChange,
  closeOnSelect = true,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [coords, setCoords] = useState({
    left: 0,
    top: 0,
    width: 0,
  });
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const errorMessage = getErrorMessage(error);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 50);
    }
  }, [isOpen, showSearch]);

  useEffect(() => {
    if (isOpen) {
      const updatePosition = () => {
        if (selectRef.current) {
          const rect = selectRef.current.getBoundingClientRect();
          setCoords({
            left: rect.left + window.scrollX,
            top: rect.bottom + window.scrollY,
            width: rect.width,
          });
        }
      };

      updatePosition();

      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideSelect =
        selectRef.current && !selectRef.current.contains(event.target);
      const isOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(event.target);

      if (isOutsideSelect && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option, e) => {
    e?.stopPropagation();
    if (!disabled && !option.disabled) {
      onChange(option.value);
      if (closeOnSelect) {
        setIsOpen(false);
      }
    }
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const dropdownMenu = (
    <motion.div
      ref={dropdownRef}
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      className="border-tertiary-20 shadow-tertiary-20/10 absolute z-[99999] mt-1 flex flex-col overflow-hidden rounded-lg border bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:shadow-none"
      style={{
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        width: `${coords.width}px`,
      }}
    >
      {/* Search Input Container */}
      {showSearch && (
        <div className="border-tertiary-20 sticky top-0 z-10 border-b bg-white/95 p-2 backdrop-blur-md dark:border-gray-700 dark:bg-gray-800/95">
          <div className="relative">
            <Search
              className="text-tertiary-40 absolute top-2 right-2.5 dark:text-gray-500"
              size={16}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchValue || ''}
              onChange={(e) => onSearchChange?.(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="جستجو..."
              className="bg-tertiary-5 border-tertiary-20 text-tertiary-80 focus:border-primary-100 focus:ring-primary-100 w-full rounded-md border py-1.5 pr-8 pl-8 text-sm transition-all focus:ring-1 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            />
            {loading && searchValue && (
              <Loader2
                className="text-primary-100 absolute top-2 left-2.5 animate-spin"
                size={16}
              />
            )}
          </div>
        </div>
      )}

      <div className="max-h-60 overflow-y-auto">
        {options.length === 0 ? (
          <div className="text-tertiary-60 px-4 py-8 text-center text-sm dark:text-gray-400">
            {emptyMessage}
          </div>
        ) : (
          options.map((option) => (
            <motion.div
              key={option.value}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.1 }}
              className={cn(
                optionVariants({
                  selected: option.value === value,
                }),
                option.disabled && 'cursor-not-allowed opacity-50',
              )}
              onClick={(e) => !option.disabled && handleSelect(option, e)}
            >
              {renderOption ? (
                renderOption(option)
              ) : (
                <>
                  <span>{option.label}</span>
                  {option.value === value && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 30,
                      }}
                    >
                      <Check
                        size={16}
                        className="text-primary-100 dark:text-primary-400"
                      />
                    </motion.div>
                  )}
                </>
              )}
            </motion.div>
          ))
        )}
      </div>

      {showManageButton && onManageClick && (
        <div
          className="border-tertiary-20 text-tertiary-80 hover:bg-tertiary-10 mt-auto flex cursor-pointer items-center gap-2 border-t px-4 py-2.5 transition-colors duration-150 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
          onClick={(e) => {
            e.stopPropagation();
            onManageClick();
            setIsOpen(false);
          }}
        >
          <Settings2 size={16} />
          <span className="text-sm font-medium">{manageButtonText}</span>
        </div>
      )}
    </motion.div>
  );

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="text-tertiary-80 mb-1.5 block text-sm font-medium dark:text-gray-300">
          {label}
        </label>
      )}

      <div ref={selectRef}>
        <div
          className={selectVariants({
            size,
            disabled,
            error: !!errorMessage,
          })}
          onClick={toggleDropdown}
        >
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {icon && (
                <span className="text-tertiary-60 flex-shrink-0 dark:text-gray-400">
                  {icon}
                </span>
              )}
              <span
                className={cn(
                  'truncate',
                  !selectedOption && 'text-tertiary-40 dark:text-gray-500',
                )}
              >
                {loading && !searchValue ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="animate-spin" size={16} />
                    در حال بارگذاری...
                  </span>
                ) : selectedOption ? (
                  renderValue ? (
                    renderValue(selectedOption)
                  ) : (
                    selectedOption.label
                  )
                ) : (
                  placeholder
                )}
              </span>
            </div>

            <motion.div
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="mr-2 flex-shrink-0"
            >
              <ChevronDown
                className="text-tertiary-60 dark:text-gray-400"
                size={16}
              />
            </motion.div>
          </div>
        </div>
      </div>

      {isMounted &&
        createPortal(
          <AnimatePresence>
            {isOpen && !disabled && dropdownMenu}
          </AnimatePresence>,
          document.body,
        )}

      {errorMessage && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-xs text-red-500 dark:text-red-400"
        >
          {errorMessage}
        </motion.p>
      )}
    </div>
  );
}

Select.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]).isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    }),
  ),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  renderOption: PropTypes.func,
  renderValue: PropTypes.func,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  showManageButton: PropTypes.bool,
  onManageClick: PropTypes.func,
  manageButtonText: PropTypes.string,
  icon: PropTypes.node,
  showSearch: PropTypes.bool,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  closeOnSelect: PropTypes.bool,
};

export default Select;
